// Part C.6 — "Boost this panel" card for low-confidence segments
import { useState } from 'react'
import { TrendingUp, Loader } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'

interface BoostTarget {
  segment_id: string
  segment_name: string
  low_confidence_count: number
  suggested_configs: number
  estimated_seconds: number
  estimated_cost_usd: number
}

interface BoostThisPanelCardProps {
  targets: BoostTarget[]
  projectId: string
  onBoostComplete?: () => void
}

export function BoostThisPanelCard({ targets, projectId, onBoostComplete }: BoostThisPanelCardProps) {
  const [running, setRunning] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  if (dismissed || targets.length === 0) return null

  const totalCost = targets.reduce((s, t) => s + t.estimated_cost_usd, 0)
  const totalSecs = Math.max(...targets.map(t => t.estimated_seconds))

  const handleBoost = async () => {
    setRunning(true)
    const token = localStorage.getItem('echo_session') ?? ''
    try {
      await fetch(`/api/v1/projects/${projectId}/twin-panel/boost`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ targets }),
      })
      onBoostComplete?.()
    } catch {
      // best effort
    } finally {
      setRunning(false)
    }
  }

  return (
    <Card data-testid="boost-this-panel" className="space-y-4 border-echo-indigo/20">
      <div className="flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-echo-indigo shrink-0" strokeWidth={1.75} />
        <h3 className="text-sm font-medium text-deep-ink">Boost this panel</h3>
      </div>

      <p className="text-sm text-slate-600">
        Some segments could use more accuracy. Running additional configs against only the low-confidence echoes (not the whole panel — cost discipline).
      </p>

      <div className="space-y-2">
        {targets.map(target => (
          <div
            key={target.segment_id}
            className="flex items-start justify-between gap-3 py-2 border-b border-border-gray/40 last:border-0"
            data-testid="boost-segment-target"
          >
            <div className="space-y-0.5">
              <p className="text-sm font-medium text-slate-700">{target.segment_name}</p>
              <p className="text-xs text-slate-400">
                {target.low_confidence_count} echoes below confidence floor
              </p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-xs text-slate-500">
                {target.suggested_configs} configs · ~{target.estimated_seconds}s · ~${target.estimated_cost_usd.toFixed(2)}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 pt-1">
        <Button
          variant="primary"
          size="sm"
          onClick={handleBoost}
          disabled={running}
          className="disabled:bg-button-disabled-bg disabled:text-button-disabled-text disabled:border-button-disabled-border"
        >
          {running ? (
            <span className="flex items-center gap-2">
              <Loader className="w-3.5 h-3.5 animate-spin" strokeWidth={1.75} />
              Running targeted boost...
            </span>
          ) : (
            `Run targeted boost  (~$${totalCost.toFixed(2)}, ~${Math.round(totalSecs)}s)`
          )}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setDismissed(true)}
          disabled={running}
        >
          Skip — accuracy is good enough
        </Button>
      </div>
    </Card>
  )
}
